import {
  useGetAccountStatementQuery,
  useLazyDownloadAccountStatementQuery,
} from "@/modules/account/services";
import { DownloadExtension } from "@/shared/common/constants";
import { DownloadType } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { FilterPanel, Statement } from "@/shared/components";
import { useFilters, useNotify } from "@/shared/hooks";
import { downloadFileFromUrl } from "@/shared/utils";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export const AccountStatement = () => {
  const { t } = useTranslation();
  const { accountId = "" } = useParams();
  const { showErrorSnackbar } = useNotify();
  const { filters, defaultFilters, setFilters } = useFilters();

  const [
    downloadAccountStatement,
    { isFetching: isDownloading },
  ] = useLazyDownloadAccountStatementQuery();

  const {
    data: accountStatement,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetAccountStatementQuery({
    id: accountId,
    ...filters
  }, {
    refetchOnMountOrArgChange: true,
  });

  const onDownload = (exportFormat: DownloadType) => {
    downloadAccountStatement({
      id: accountId,
      exportFormat,
      ...filters,
    })
      .unwrap()
      .then((response) => {
        if (response?.url) {
          const fileType = DownloadExtension[exportFormat];
          const name = `Account-statements-${filters.startDate}-${filters.endDate}.${fileType}`;
          downloadFileFromUrl(response.url, name);
        }
      })
      .catch((error) => {
        showErrorSnackbar(error);
      });
  };

  const actions = [
    {
      label: t("general.pdf"),
      type: DownloadType.Pdf,
    },
    {
      label: t("general.csv"),
      type: DownloadType.Csv,
    },
    {
      label: t("general.cb"),
      type: DownloadType.Excel,
    }
  ];

  return (
    <Statement
      onDownload={onDownload}
      isFetching={isFetching}
      isError={isError}
      actions={actions}
      isDownloading={isDownloading}
      error={error as IError}
      refetch={refetch}
      statement={accountStatement}
    >
      <FilterPanel
        filters={filters}
        setFilters={setFilters}
        defaultFilters={defaultFilters}
      />
    </Statement>
  );
};
